<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-toolbar
            density="compact"
            color="primary"
          >
            <v-toolbar-title>
              <v-icon
                :icon="mdiViewDashboard"
                class="mr-2"
              />
              {{ t('dashboard.title') }}
            </v-toolbar-title>
            <v-spacer />
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              :label="t('dashboard.year')"
              density="compact"
              hide-details
              class="mr-4"
              style="max-width: 150px"
              :disabled="isLoading"
              @update:model-value="loadData"
            >
              <template #prepend-inner>
                <v-icon
                  :icon="mdiCalendar"
                  size="small"
                />
              </template>
            </v-select>
            <v-select
              v-model="selectedOrganizationalUnit"
              :items="organizationalUnitsWithAll"
              item-title="name"
              item-value="id"
              return-object
              :label="t('dashboard.office')"
              :disabled="isLoading"
              hide-details
              density="compact"
              @update:model-value="loadData"
            />
            <v-tooltip
              location="bottom"
              :text="t('dashboard.refresh')"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  :loading="isLoading"
                  icon
                  variant="text"
                  @click="loadData"
                >
                  <v-icon :icon="mdiRefresh" />
                </v-btn>
              </template>
            </v-tooltip>
          </v-toolbar>

          <v-progress-linear
            :active="isLoading"
            :indeterminate="isLoading"
            color="primary"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- KPI Cards -->
    <v-row>
      <v-col
        cols="12"
        md="3"
      >
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="text-overline">
                {{ t('dashboard.productivity.title') }}
              </div>
              <v-menu
                v-model="showProductivityInfo"
                :close-on-content-click="false"
                location="bottom"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    :icon="mdiInformationOutline"
                    variant="text"
                    size="x-small"
                    density="compact"
                  />
                </template>
                <v-card
                  max-width="450"
                  class="pa-4"
                >
                  <h3 class="text-subtitle-2 mb-2">
                    {{ t('dashboard.productivity.infoTitle') }}
                  </h3>
                  <p class="text-caption mb-3">
                    {{ t('dashboard.productivity.infoText') }}
                  </p>
                  <div class="text-caption font-weight-bold mb-1">
                    {{ t('dashboard.productivity.calculation') }}
                  </div>
                  <code class="text-caption d-block mb-3">Math.round((billableHours / totalHours - verlof) * 100)</code>
                  <p class="text-caption mb-2">
                    {{ t('dashboard.productivity.infoFooter') }}
                  </p>
                </v-card>
              </v-menu>
            </div>
            <div class="text-h4 text-primary">
              {{ averageProductivity }}%
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ t('dashboard.productivity.subtitle', { year: selectedYear, billable: formatNumber(billableHours), total: formatNumber(totalHours) }) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="3"
      >
        <v-card>
          <v-card-text>
            <div class="text-overline mb-1">
              {{ t('dashboard.totalHours.title') }}
            </div>
            <div class="text-h4 text-success">
              {{ formatNumber(totalHours) }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ t('dashboard.totalHours.subtitle', { billable: formatNumber(billableHours) }) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="3"
      >
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="text-overline">
                {{ t('dashboard.projects.title') }}
              </div>
              <v-menu
                v-model="showProjectsInfo"
                :close-on-content-click="false"
                location="bottom"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    :icon="mdiInformationOutline"
                    variant="text"
                    size="x-small"
                    density="compact"
                  />
                </template>
                <v-card
                  max-width="450"
                  class="pa-4"
                >
                  <h3 class="text-subtitle-2 mb-2">
                    {{ t('dashboard.projects.infoTitle') }}
                  </h3>
                  <p class="text-caption mb-3">
                    {{ t('dashboard.projects.infoText') }}
                  </p>
                  <div class="text-caption font-weight-bold mb-1">
                    {{ t('dashboard.whatCounts') }}
                  </div>
                  <ul
                    class="text-caption mb-3"
                    style="padding-left: 20px; line-height: 1.6;"
                  >
                    <li>{{ t('dashboard.projects.counts1') }}</li>
                    <li>{{ t('dashboard.projects.counts2') }}</li>
                    <li>{{ t('dashboard.projects.counts3') }}</li>
                  </ul>
                  <p class="text-caption mb-2">
                    {{ t('dashboard.projects.infoFooter') }}
                  </p>
                </v-card>
              </v-menu>
            </div>
            <div class="text-h4 text-info">
              {{ activeProjects }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ selectedYear }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="3"
      >
        <v-card>
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="text-overline">
                {{ t('dashboard.employees.title') }}
              </div>
              <v-menu
                v-model="showEmployeesInfo"
                :close-on-content-click="false"
                location="bottom"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    :icon="mdiInformationOutline"
                    variant="text"
                    size="x-small"
                    density="compact"
                  />
                </template>
                <v-card
                  max-width="450"
                  class="pa-4"
                >
                  <h3 class="text-subtitle-2 mb-2">
                    {{ t('dashboard.employees.infoTitle') }}
                  </h3>
                  <p class="text-caption mb-3">
                    {{ t('dashboard.employees.infoText') }}
                  </p>
                  <div class="text-caption font-weight-bold mb-1">
                    {{ t('dashboard.whatCounts') }}
                  </div>
                  <ul
                    class="text-caption mb-3"
                    style="padding-left: 20px; line-height: 1.6;"
                  >
                    <li>{{ t('dashboard.employees.counts1') }}</li>
                    <li>{{ t('dashboard.employees.counts2') }}</li>
                    <li>{{ t('dashboard.employees.counts3') }}</li>
                  </ul>
                  <p class="text-caption mb-2">
                    {{ t('dashboard.employees.infoFooter') }}
                  </p>
                </v-card>
              </v-menu>
            </div>
            <div class="text-h4 text-warning">
              {{ activeEmployees }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ t('dashboard.employees.subtitle', { year: selectedYear }) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row 1 -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon
              :icon="mdiChartLine"
              class="mr-2"
            />
            {{ t('dashboard.chart.title', { year: selectedYear }) }}
          </v-card-title>
          <v-card-text>
            <ag-charts
              v-if="weeklyBillabilityData.length > 0"
              :options="weeklyBillabilityChartOptions as any"
              style="height: 400px"
            />
            <v-alert
              v-else
              type="info"
              variant="tonal"
            >
              {{ t('dashboard.chart.noData') }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { AgCharts } from 'ag-charts-vue3'
import { getWeeklyProductivity, getActiveProjects, getActiveEmployees, getOrganizationalUnits } from '@/services/backendApiService'
import { formatHours } from '@/utils/numberUtils'
import { useI18n } from 'vue-i18n'

import {
  mdiViewDashboard,
  mdiRefresh,
  mdiCalendar,
  mdiChartLine,
  mdiInformationOutline
} from '@mdi/js'

type OrgUnitOption = { id: string; name: string }

// Eén rij in de weekgrafiek (huidig jaar + vorig jaar voor de tooltip)
interface WeekRow {
  week: string
  weekNumber: number
  billabilityCurrent: number | null
  billabilityPrevious: number | null
  billableHours: number
  nonBillableHours: number
  verlofHours: number
  activeEmployees: number
  billableHoursPrevious: number
  nonBillableHoursPrevious: number
  verlofHoursPrevious: number
}

type BillabilityKey = 'billabilityCurrent' | 'billabilityPrevious'

const { t, locale } = useI18n()

// Afgerond getal met duizendtalscheiding volgens de actieve taal
const formatNumber = (value: number) =>
  Math.round(value).toLocaleString(locale.value === 'en' ? 'en-US' : 'nl-NL')

// State
const isLoading = ref(false)
const selectedYear = ref(new Date().getFullYear())
// Default: alle BV's met return-object. De getoonde naam komt uit organizationalUnitsWithAll (match op id).
const selectedOrganizationalUnit = ref<OrgUnitOption>({ id: 'all', name: 'Alle' })
const organizationalUnits = ref<OrgUnitOption[]>([])
const weeklyProductivityData = ref<Array<{
  week: number;
  year: number;
  productivity: number;
  billableHours: number;
  nonBillableHours: number;
  verlofHours: number;
  totalHours: number;
  activeEmployees: number;
}>>([]) // Van backend
const yearMetrics = ref<Record<number, { activeProjects: number; activeEmployees: number }>>({}) // Metrics per jaar
const showProductivityInfo = ref(false)
const showProjectsInfo = ref(false)
const showEmployeesInfo = ref(false)

async function loadOrganizationalUnits() {
  try {
    const units = await getOrganizationalUnits()
    organizationalUnits.value = units
  } catch {
    // Failed to load organizational units
  }
}

// Theme Management
import { useTheme } from 'vuetify'
const theme = useTheme()

const isDarkMode = computed(() =>
  theme.global.name.value === 'myCustomDarkTheme' ||
  theme.global.name.value === 'kerstTheme'
)

const chartLineColor = computed(() => isDarkMode.value ? '#90caf9' : '#1976D2')
const barColors = computed(() => ({
  billable: isDarkMode.value ? '#66BB6A' : '#4CAF50',
  nonBillable: isDarkMode.value ? '#FFA726' : '#FF9800',
  verlof: isDarkMode.value ? '#42A5F5' : '#2196F3'
}))

const availableYears = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  // Start vanaf 2025, niet ouder
  for (let year = currentYear; year >= 2025; year--) {
    years.push(year)
  }
  return years
})

// Organizational units met 'Alle BV's' optie
const organizationalUnitsWithAll = computed(() => {
  return [
    { id: 'all', name: t('dashboard.allOffices') },
    ...(organizationalUnits.value || [])
  ]
})

const billableHours = computed(() => {
  return weeklyProductivityData.value
    .filter(d => d.year === selectedYear.value)
    .reduce((sum, d) => sum + d.billableHours, 0)
})

const totalHours = computed(() => {
  return weeklyProductivityData.value
    .filter(d => d.year === selectedYear.value)
    .reduce((sum, d) => sum + d.totalHours, 0)
})

const averageProductivity = computed(() => {
  // Berekening: totaal billable uren / totaal relevante uren * 100
  // Relevante uren = billable + non-billable (verlof wordt uitgesloten)
  const currentYearData = weeklyProductivityData.value.filter(d => d.year === selectedYear.value)

  if (currentYearData.length === 0) return 0

  const totalBillable = currentYearData.reduce((sum, d) => sum + d.billableHours, 0)
  const totalRelevant = currentYearData.reduce((sum, d) => sum + d.billableHours + d.nonBillableHours, 0)

  if (totalRelevant === 0) return 0

  return Math.round((totalBillable / totalRelevant) * 100)
})

const activeProjects = computed(() => {
  const metrics = yearMetrics.value[selectedYear.value]
  return metrics?.activeProjects ?? 0
})

const activeEmployees = computed(() => {
  const metrics = yearMetrics.value[selectedYear.value]
  return metrics?.activeEmployees ?? 0
})

// Helper: Haal wekelijkse productiviteit data op van backend
// async function fetchWeeklyProductivity(years: number[], organizationalUnitId?: string): Promise<{
//   data: Array<{
//     week: number;
//     year: number;
//     productivity: number;
//     billableHours: number;
//     nonBillableHours: number;
//     verlofHours: number;
//     totalHours: number;
//     activeEmployees: number;
//   }>;
//   metrics: Record<number, { activeProjects: number; activeEmployees: number }>;
// }> {
//   try {
//     const response = await getWeeklyProductivity(years, organizationalUnitId)
//     return {
//       data: response.data || [],
//       metrics: response.metrics || {}
//     }
//   } catch (error: any) {
//     console.error('Failed to fetch weekly productivity from backend:', error.message)
//     return { data: [], metrics: {} }
//   }
// }
// Chart Data - Weekly Billability (current vs previous year)
// Data komt nu van backend endpoint
const weeklyBillabilityData = computed(() => {
  return weeklyProductivityData.value
    .filter(d => d.year === selectedYear.value)
    .map(d => ({
      week: d.week,
      billability: d.productivity,
      year: d.year
    }))
})

const previousYearBillabilityData = computed(() => {
  const previousYear = selectedYear.value - 1
  return weeklyProductivityData.value
    .filter(d => d.year === previousYear)
    .map(d => ({
      week: d.week,
      billabilityPrevious: d.productivity,
      year: d.year
    }))
})

const combinedWeeklyData = computed(() => {
  const merged: WeekRow[] = []
  const previousYear = selectedYear.value - 1

  // ISO 8601: weken lopen van 1 t/m 53
  for (let week = 1; week <= 53; week++) {
    const current = weeklyBillabilityData.value.find(d => d.week === week)
    const previous = previousYearBillabilityData.value.find(d => d.week === week)

    // Haal uren verdeling op van backend data (als beschikbaar) voor BEIDE jaren
    const backendData = weeklyProductivityData.value.find(d => d.week === week && d.year === selectedYear.value)
    const backendDataPrevious = weeklyProductivityData.value.find(d => d.week === week && d.year === previousYear)

    // Sla weken zonder data over (geen uren en geen productiviteit)
    const hasData = backendData && (backendData.billableHours > 0 || backendData.nonBillableHours > 0 || backendData.verlofHours > 0)
    if (!hasData && !current?.billability && !previous?.billabilityPrevious) {
      continue
    }

    merged.push({
      week: `W${week}`,       // String voor category axis (W1, W2, W3...)
      weekNumber: week,       // Number voor sorting/filtering
      billabilityCurrent: current?.billability ?? null,
      billabilityPrevious: previous?.billabilityPrevious ?? null,
      billableHours: backendData?.billableHours ?? 0,
      nonBillableHours: backendData?.nonBillableHours ?? 0,
      verlofHours: backendData?.verlofHours ?? 0,
      activeEmployees: backendData?.activeEmployees ?? 0,
      // Vorig jaar uren (voor tooltip)
      billableHoursPrevious: backendDataPrevious?.billableHours ?? 0,
      nonBillableHoursPrevious: backendDataPrevious?.nonBillableHours ?? 0,
      verlofHoursPrevious: backendDataPrevious?.verlofHours ?? 0
    })
  }

  return merged
})

// Chart Options - Weekly Billability (combined with hours breakdown)
const weeklyBillabilityChartOptions = computed(() => ({
  data: combinedWeeklyData.value,
  theme: isDarkMode.value ? 'ag-material-dark' : 'ag-material',
  tooltip: {
    mode: 'shared',
    renderer: ({ datum }: { datum: WeekRow }) => {
      const total = datum.billableHours + datum.nonBillableHours + datum.verlofHours
      const employees = datum.activeEmployees || 0
      const weekLabel = t('dashboard.chart.week', { week: datum.weekNumber })
      const h = t('dashboard.hoursSuffix')

      return {
        heading: weekLabel,
        data: [
          { label: t('dashboard.chart.billable'), value: `${formatHours(datum.billableHours, 1)}${h}` },
          { label: t('dashboard.chart.nonBillable'), value: `${formatHours(datum.nonBillableHours, 1)}${h}` },
          { label: t('dashboard.chart.leave'), value: `${formatHours(datum.verlofHours, 1)}${h}` },
          { label: t('dashboard.chart.total'), value: `${formatHours(total, 1)}${h}` },
          { label: t('dashboard.chart.employees'), value: employees }
        ]
      }
    }
  },
  series: [
    // Staafdiagram: Declarabele uren
    {
      type: 'bar',
      xKey: 'week',
      yKey: 'billableHours',
      yName: t('dashboard.chart.billableSeries'),
      fill: barColors.value.billable,
      fillOpacity: 0.6,
      stacked: true,
      strokeWidth: 0
    },
    // Staafdiagram: Non-declarabele uren
    {
      type: 'bar',
      xKey: 'week',
      yKey: 'nonBillableHours',
      yName: t('dashboard.chart.nonBillableSeries'),
      fill: barColors.value.nonBillable,
      fillOpacity: 0.6,
      stacked: true,
      strokeWidth: 0
    },
    // Staafdiagram: Verlof uren
    {
      type: 'bar',
      xKey: 'week',
      yKey: 'verlofHours',
      yName: t('dashboard.chart.leaveSeries'),
      fill: barColors.value.verlof,
      fillOpacity: 0.6,
      stacked: true,
      strokeWidth: 0
    },
    // Lijndiagram: Productiviteit huidig jaar
    {
      type: 'line',
      xKey: 'week',
      yKey: 'billabilityCurrent',
      yName: t('dashboard.chart.productivityYear', { year: selectedYear.value }),
      stroke: chartLineColor.value,
      strokeWidth: 3,
      marker: {
        enabled: true,
        size: 6,
        shape: 'circle',
        fill: chartLineColor.value,
        stroke: chartLineColor.value
      },
      // Chart line color adapts to theme
      tooltip: {
        renderer: ({ datum, yKey }: { datum: WeekRow; yKey: BillabilityKey }) => {
          const value = datum[yKey]
          if (value === null || value === undefined) return { data: [] }
          const totalHours = (datum.billableHours || 0) + (datum.nonBillableHours || 0) + (datum.verlofHours || 0)

          return {
            title: `${datum.week} - ${t('dashboard.chart.productivityYear', { year: selectedYear.value })}`,
            data: [
              { label: t('dashboard.chart.productivity'), value: `${value.toFixed(1)}%` },
              { label: t('dashboard.chart.totalHours'), value: `${formatHours(totalHours, 1)}${t('dashboard.hoursSuffix')}` }
            ]
          }
        }
      }
    },
    // Lijndiagram: Productiviteit vorig jaar
    {
      type: 'line',
      xKey: 'week',
      yKey: 'billabilityPrevious',
      yName: t('dashboard.chart.productivityYear', { year: selectedYear.value - 1 }),
      stroke: isDarkMode.value ? '#BDBDBD' : '#757575',
      strokeWidth: 2,
      strokeOpacity: 0.8,
      marker: {
        enabled: true,
        size: 4,
        shape: 'circle',
        fill: isDarkMode.value ? '#BDBDBD' : '#757575',
        stroke: isDarkMode.value ? '#BDBDBD' : '#757575'
      },
      lineDash: [5, 5],
      tooltip: {
        renderer: ({ datum, yKey }: { datum: WeekRow; yKey: BillabilityKey }) => {
          const value = datum[yKey]
          if (value === null || value === undefined) return { data: [] }
          const totalHours = (datum.billableHoursPrevious || 0) + (datum.nonBillableHoursPrevious || 0) + (datum.verlofHoursPrevious || 0)
          return {
            title: `${datum.week} - ${t('dashboard.chart.productivityYear', { year: selectedYear.value - 1 })}`,
            data: [
              { label: t('dashboard.chart.productivity'), value: `${value.toFixed(1)}%` },
              { label: t('dashboard.chart.totalHours'), value: `${formatHours(totalHours, 1)}${t('dashboard.hoursSuffix')}` }
            ]
          }
        }
      }
    }
  ],
  axes: [
    {
      type: 'category',
      position: 'bottom',
      title: {
        text: t('dashboard.chart.weekNumber'),
        fontWeight: 'bold'
      },
      label: {
        rotation: 0
      },
      gridLine: {
        enabled: true,
        style: [{ stroke: '#f0f0f0', lineDash: [3, 3] }]
      }
    },
    {
      type: 'number',
      position: 'left',
      keys: ['billableHours', 'nonBillableHours', 'verlofHours'],
      title: {
        text: t('dashboard.chart.hoursPerWeek'),
        fontWeight: 'bold'
      },
      label: {
        formatter: ({ value }: { value: number }) => `${value}${t('dashboard.hoursSuffix')}`
      },
      gridLine: {
        enabled: false
      }
    },
    {
      type: 'number',
      position: 'right',
      keys: ['billabilityCurrent', 'billabilityPrevious'],
      title: {
        text: t('dashboard.chart.productivityAxis'),
        fontWeight: 'bold'
      },
      min: 0,
      max: 100,
      tick: {
        interval: 10
      },
      label: {
        formatter: ({ value }: { value: number }) => `${value}%`
      },
      gridLine: {
        enabled: true,
        style: [{ stroke: isDarkMode.value ? '#424242' : '#f0f0f0' }]
      },
      crossLines: [
        {
          type: 'line',
          value: 80,
          stroke: isDarkMode.value ? '#66BB6A' : '#4CAF50',
          strokeWidth: 2,
          strokeOpacity: 0.3,
          lineDash: [8, 4],
          label: {
            text: t('dashboard.chart.target'),
            position: 'top',
            fontSize: 11,
            color: '#4CAF50',
            fontWeight: 'bold',
            padding: 24
          }
        }
      ]
    }
  ],
  legend: {
    enabled: true,
    position: 'bottom',
    spacing: 20
  },
  padding: {
    top: 20,
    right: 30,
    bottom: 20,
    left: 20
  }
}))

// Data Loading
async function loadData() {
  isLoading.value = true

  try {
    // Bepaal welke jaren op te halen - alleen vanaf 2025
    const yearsToFetch = [selectedYear.value]
    const previousYear = selectedYear.value - 1
    if (previousYear >= 2025) {
      yearsToFetch.push(previousYear)
    }

    // Haal alle data op van backend (parallel queries voor performance)
    // Voor projects filteren we op de bedrijfsnaam (uit het geselecteerde object)
    let businessCentralCompanyName: string | undefined = undefined
    let orgUnitId: string | undefined = undefined

    if (selectedOrganizationalUnit.value?.id !== 'all') {
      businessCentralCompanyName = selectedOrganizationalUnit.value?.name
      orgUnitId = selectedOrganizationalUnit.value?.id
    }

    // Voer drie queries parallel uit
    const [productivityResponse, projectsResponse, employeesResponse] = await Promise.all([
      getWeeklyProductivity(yearsToFetch, orgUnitId),
      getActiveProjects(yearsToFetch, businessCentralCompanyName),
      getActiveEmployees(yearsToFetch, orgUnitId)
    ])

    weeklyProductivityData.value = productivityResponse.data

    // Transformeer projects en employees data naar metrics object
    const projectsMap = new Map<number, number>(
      projectsResponse.data?.map((item: { year: number; activeProjects: number }) => [item.year, item.activeProjects])
    )
    const employeesMap = new Map<number, number>(
      employeesResponse.data?.map((item: { year: number; activeEmployees: number }) => [item.year, item.activeEmployees])
    )

    yearMetrics.value = yearsToFetch.reduce<Record<number, { activeProjects: number; activeEmployees: number }>>((acc, year) => {
      acc[year] = {
        activeProjects: projectsMap.get(year) || 0,
        activeEmployees: employeesMap.get(year) || 0
      }
      return acc
    }, {})

  } catch {
    // Error handling - data loading failed
  } finally {
    isLoading.value = false
  }
}

// Watch year changes
watch(selectedYear, () => {
  loadData()
})

// Watch organizational unit changes
watch(selectedOrganizationalUnit, () => {
  loadData()
})

// Lifecycle
onMounted(async () => {
  await loadOrganizationalUnits()
  await loadData()
})

// Route meta voor admin check
definePage({
  meta: {
    requiresAdmin: true
  }
})
</script>

<style scoped>
.text-h4 {
  font-weight: 600;
}
</style>
